import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { gamesAPI, zbioAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CreateGameModal from './CreateGameModal';
import ProfileAvatar from './ProfileAvatar';
import socketService from '../services/socket';

interface Game {
  id: string;
  name: string;
  hostName: string;
  hostId?: string;
  type: 'public' | 'private' | 'friends';
  gameVariant: string;
  status: 'waiting' | 'playing' | 'finished';
  maxPlayers: number;
  players: string[];
  createdAt: string;
}

interface VariantConfig {
  key: string;
  label: string;
  icon: string;
  players: number[];
  defaultPlayers: number;
  decks: number;
  dealtCards: number;
  cardsPerDeal: number;
  gamePoints: number;
}

const QUICK_PLAY_VARIANTS: VariantConfig[] = [
  { key: 'lower_green', label: 'Pharaon', icon: '🃏', players: [2, 3, 4, 5, 6], defaultPlayers: 4, decks: 1, dealtCards: 4, cardsPerDeal: 2, gamePoints: 100 },
  { key: 'raub', label: 'Raub', icon: '⚔️', players: [4], defaultPlayers: 4, decks: 1, dealtCards: 4, cardsPerDeal: 2, gamePoints: 100 },
  { key: 'sedmica', label: 'Sedmica', icon: '7️⃣', players: [2, 4], defaultPlayers: 4, decks: 1, dealtCards: 4, cardsPerDeal: 2, gamePoints: 120 },
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [publicGames, setPublicGames] = useState<Game[]>([]);
  const [myGames, setMyGames] = useState<Game[]>([]);
  const [friendsGames, setFriendsGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userBalance, setUserBalance] = useState<number>(0);
  const [expanded, setExpanded] = useState<{ public: boolean; my: boolean; friends: boolean }>({
    public: false,
    my: false,
    friends: false,
  });
  const [matchmaking, setMatchmaking] = useState<string | null>(null);

  const toggleSection = (id: 'public' | 'my' | 'friends') =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    loadGames();
    zbioAPI.getBalance()
      .then(res => setUserBalance(res.data.balance))
      .catch(() => {});
    
    // Connect to WebSocket for real-time updates
    const token = localStorage.getItem('token');
    if (token) {
      const socket = socketService.connect(token);
      
      // Listen for game updates
      socket.on('gameUpdate', (data: any) => {
        console.log('Game update received:', data);
        
        switch (data.type) {
          case 'created':
            // Add new game to appropriate list
            if (data.game.type === 'public' && data.game.status === 'waiting') {
              setPublicGames(prev => [data.game, ...prev]);
            }
            if (data.game.hostId === user?.id) {
              setMyGames(prev => [data.game, ...prev]);
            }
            break;
            
          case 'updated':
            // Update existing game in all lists
            setPublicGames(prev => 
              prev.map(game => game.id === data.game.id ? data.game : game)
            );
            setMyGames(prev => 
              prev.map(game => game.id === data.game.id ? data.game : game)
            );
            setFriendsGames(prev => 
              prev.map(game => game.id === data.game.id ? data.game : game)
            );
            break;
            
          case 'deleted':
            // Remove game from all lists
            setPublicGames(prev => prev.filter(game => game.id !== data.gameId));
            setMyGames(prev => prev.filter(game => game.id !== data.gameId));
            setFriendsGames(prev => prev.filter(game => game.id !== data.gameId));
            break;
            
          case 'joined':
          case 'left':
          case 'playerSat':
          case 'playerLeftTable':
          case 'started':
            // Reload games to get updated state
            loadGames();
            break;
        }
      });
      
      // Cleanup on unmount
      return () => {
        socketService.disconnect();
      };
    }
  }, [user?.id]);

  const loadGames = async () => {
    try {
      setLoading(true);
      const response = await gamesAPI.getAll();
      const games = response.data;

      setPublicGames(games.filter((g: Game) => g.type === 'public' && g.status === 'waiting'));
      setMyGames(games.filter((g: Game) => g.players.includes(user?.id || '')));
      setFriendsGames(games.filter((g: Game) => g.type === 'friends' && g.status === 'waiting'));
    } catch (error) {
      console.error('Error loading games:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinGame = (gameId: string) => {
    navigate(`/games/${gameId}`);
  };

  const handleQuickPlay = async (variant: VariantConfig) => {
    if (!user) return;
    const maxPlayers = variant.defaultPlayers;
    const key = variant.key;
    setMatchmaking(key);
    try {
      // 1) Look for an existing public, waiting game of this variant + size with an open seat
      const res = await gamesAPI.getAll();
      const games: Game[] = res.data || [];
      const candidates = games.filter(
        g =>
          g.type === 'public' &&
          g.status === 'waiting' &&
          g.gameVariant === variant.key &&
          g.maxPlayers === maxPlayers &&
          (g.players?.length || 0) < g.maxPlayers
      );
      // Prefer a game someone else hosts so we actually match players
      const target = candidates.find(g => !g.players?.includes(user.id)) || candidates[0];

      if (target) {
        try { await gamesAPI.join(target.id, false); } catch (_) { /* may already be in */ }
        try { await gamesAPI.sit(target.id); } catch (_) { /* seat may be taken */ }
        navigate(`/games/${target.id}`);
        return;
      }

      // 2) None found - create a fresh public game and head into it
      const gameData = {
        name: `${user.firstName} ${user.lastName} - ${variant.label} - ${maxPlayers} players - Friendly`,
        maxPlayers,
        decks: variant.decks,
        dealtCards: variant.dealtCards,
        cardsPerDeal: variant.cardsPerDeal,
        gamePoints: variant.gamePoints,
        type: 'public',
        gameVariant: variant.key,
        gameMode: 'friendly',
        entryFee: 0,
        platformFee: 5,
      };
      const created = await gamesAPI.create(gameData);
      const newId = created.data?.id;
      if (newId) {
        navigate(`/games/${newId}`);
      } else {
        loadGames();
      }
    } catch (error) {
      console.error('Quick play error:', error);
    } finally {
      setMatchmaking(null);
    }
  };

  const handleCreateGame = async (gameData: any) => {
    try {
      await gamesAPI.create(gameData);
      setShowCreateModal(false);
      loadGames();
    } catch (error) {
      console.error('Error creating game:', error);
    }
  };

  const handleDeleteGame = async (e: React.MouseEvent, gameId: string) => {
    e.stopPropagation();
    if (!window.confirm(t('dashboard.confirmDeleteGame'))) return;
    try {
      await gamesAPI.delete(gameId);
      setMyGames(prev => prev.filter(g => g.id !== gameId));
      setPublicGames(prev => prev.filter(g => g.id !== gameId));
      setFriendsGames(prev => prev.filter(g => g.id !== gameId));
    } catch (error) {
      console.error('Error deleting game:', error);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return t('dashboard.justNow');
    if (diffMins < 60) return t('dashboard.minutesAgo', { count: diffMins });
    if (diffMins < 1440) return t('dashboard.hoursAgo', { count: Math.floor(diffMins / 60) });
    return t('dashboard.daysAgo', { count: Math.floor(diffMins / 1440) });
  };

  const getGameVariantLabel = (variant: string) => {
    if (variant === 'lower_green') return 'Pharaon';
    if (variant === 'sedmica') return 'Sedmica';
    return 'Raub';
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="dashboard-modern">
      {/* User Header */}
      <div className="dashboard-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <ProfileAvatar user={user} size={64} onClick={() => navigate('/profile')} />
          <div>
            <h1>{user.firstName} {user.lastName}</h1>
            <p>{user.email}</p>
            <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
              <span style={{ color: '#00c853', fontWeight: '600', cursor: 'pointer' }} onClick={() => navigate('/wallet')}>
                💰 {userBalance.toLocaleString()} ZBIO
              </span>
              <span style={{ color: '#769656', fontWeight: '600' }}>
                🟢 {t('dashboard.online')}
              </span>
            </div>
          </div>
        </div>
      </div>

        {/* Quick Play - matchmaking */}
        <div className="quick-play">
          <h3 className="quick-play-title">⚡ {t('dashboard.quickPlayTitle')}</h3>
          <p className="quick-play-subtitle">{t('dashboard.quickPlaySubtitle')}</p>
          <div className="quick-play-grid quick-play-grid--simple">
            {QUICK_PLAY_VARIANTS.map((variant) => {
              const isBusy = matchmaking === variant.key;
              return (
                <button
                  key={variant.key}
                  className="quick-play-btn quick-play-btn--solo"
                  onClick={() => handleQuickPlay(variant)}
                  disabled={!!matchmaking}
                >
                  <span className="quick-play-icon">{variant.icon}</span>
                  <span className="quick-play-name">{variant.label}</span>
                  <span className="quick-play-meta">
                    {isBusy ? t('dashboard.matching') : t('dashboard.playNow')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="game-actions dash-quick-actions">
          <button
            className="action-btn action-btn-xl"
            onClick={() => setShowCreateModal(true)}
          >
            <span>➕</span>
            {t('dashboard.createGame')}
          </button>
          <button
            className="action-btn"
            onClick={() => navigate('/friends')}
            style={{ background: '#2196F3' }}
          >
            <span>👥</span>
            {t('dashboard.findFriends')}
          </button>
          <button
            className="action-btn"
            onClick={() => navigate('/history')}
            style={{ background: '#FF9800' }}
          >
            <span>📊</span>
            {t('dashboard.viewHistory')}
          </button>
        </div>

        {/* Public Games */}
        <div className="game-section">
          <button className="section-accordion-header" onClick={() => toggleSection('public')}>
            <span className="section-accordion-title">
              <span>🌍</span> {t('dashboard.publicGames')} ({publicGames.length})
            </span>
            <span className={`section-chevron ${expanded.public ? 'open' : ''}`}>▾</span>
          </button>

          {expanded.public && (
            loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#a0a0a0' }}>
                {t('dashboard.loadingGames')}
              </div>
            ) : publicGames.length === 0 ? (
              <div className="game-card modern">
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🎮</div>
                  <h4 style={{ color: '#ffffff', marginBottom: '8px' }}>{t('dashboard.noPublicGames')}</h4>
                  <p style={{ color: '#a0a0a0', margin: 0 }}>{t('dashboard.noPublicGamesDesc')}</p>
                </div>
              </div>
            ) : (
              <div className="games-grid">
                {publicGames.map((game) => (
                  <div key={game.id} className="game-card modern" onClick={() => handleJoinGame(game.id)}>
                    <div className="game-header">
                      <span className={`game-status ${game.status}`}>{t(`dashboard.${game.status}`).toUpperCase()}</span>
                    </div>
                    <div className="game-name-section">
                      <h4 className="game-name">{game.name}</h4>
                      <p className="game-description">{t('dashboard.hostedBy', { name: game.hostName })}</p>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.gameType')}</span>
                      <span>{getGameVariantLabel(game.gameVariant)}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.players')}</span>
                      <span>{(game.players?.length || 0)}/{game.maxPlayers}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.created')}</span>
                      <span>{formatTimeAgo(game.createdAt)}</span>
                    </div>
                    <button className="game-card-join-btn">▶ {t('dashboard.joinGame')}</button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* My Games */}
        <div className="game-section">
          <button className="section-accordion-header" onClick={() => toggleSection('my')}>
            <span className="section-accordion-title">
              <span>🎯</span> {t('dashboard.myGames')} ({myGames.length})
            </span>
            <span className={`section-chevron ${expanded.my ? 'open' : ''}`}>▾</span>
          </button>

          {expanded.my && (
            myGames.length === 0 ? (
              <div className="game-card modern">
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>🎲</div>
                  <h4 style={{ color: '#ffffff', marginBottom: '8px' }}>{t('dashboard.noGamesYet')}</h4>
                  <p style={{ color: '#a0a0a0', margin: 0 }}>{t('dashboard.noGamesYetDesc')}</p>
                </div>
              </div>
            ) : (
              <div className="games-grid">
                {myGames.map((game) => (
                  <div key={game.id} className="game-card modern" onClick={() => handleJoinGame(game.id)}>
                    <div className="game-header">
                      <span className={`game-status ${game.status}`}>{t(`dashboard.${game.status}`).toUpperCase()}</span>
                      {game.hostId === user?.id && (
                        <button
                          className="game-delete-btn"
                          onClick={(e) => handleDeleteGame(e, game.id)}
                          title={t('dashboard.deleteGameTitle')}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <div className="game-name-section">
                      <h4 className="game-name">{game.name}</h4>
                      <p className="game-description">{getGameVariantLabel(game.gameVariant)}</p>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.gameType')}</span>
                      <span>{getGameVariantLabel(game.gameVariant)}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.players')}</span>
                      <span>{(game.players?.length || 0)}/{game.maxPlayers}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.created')}</span>
                      <span>{formatTimeAgo(game.createdAt)}</span>
                    </div>
                    <button className="game-card-join-btn">▶ {t('dashboard.openGame')}</button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Friends Games */}
        <div className="game-section">
          <button className="section-accordion-header" onClick={() => toggleSection('friends')}>
            <span className="section-accordion-title">
              <span>👥</span> {t('dashboard.friendsGames')} ({friendsGames.length})
            </span>
            <span className={`section-chevron ${expanded.friends ? 'open' : ''}`}>▾</span>
          </button>

          {expanded.friends && (
            friendsGames.length === 0 ? (
              <div className="game-card modern">
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>👥</div>
                  <h4 style={{ color: '#ffffff', marginBottom: '8px' }}>{t('dashboard.noFriendsGames')}</h4>
                  <p style={{ color: '#a0a0a0', margin: 0 }}>{t('dashboard.noFriendsGamesDesc')}</p>
                </div>
              </div>
            ) : (
              <div className="games-grid">
                {friendsGames.map((game) => (
                  <div key={game.id} className="game-card modern" onClick={() => handleJoinGame(game.id)}>
                    <div className="game-header">
                      <span className={`game-status ${game.status}`}>{t(`dashboard.${game.status}`).toUpperCase()}</span>
                    </div>
                    <div className="game-name-section">
                      <h4 className="game-name">{game.name}</h4>
                      <p className="game-description">{t('dashboard.by', { name: game.hostName })}</p>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.gameType')}</span>
                      <span>{getGameVariantLabel(game.gameVariant)}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.players')}</span>
                      <span>{(game.players?.length || 0)}/{game.maxPlayers}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('dashboard.created')}</span>
                      <span>{formatTimeAgo(game.createdAt)}</span>
                    </div>
                    <button className="game-card-join-btn">▶ {t('dashboard.joinGame')}</button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Quick Stats (moved to bottom) */}
        <div className="stats-grid" style={{ marginTop: '40px' }}>
          <div className="stat-card">
            <div className="stat-value">{myGames.length}</div>
            <div className="stat-label">{t('dashboard.statMyGames')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{publicGames.length}</div>
            <div className="stat-label">{t('dashboard.statPublicGames')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">0</div>
            <div className="stat-label">{t('dashboard.statFriendsOnline')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">0</div>
            <div className="stat-label">{t('dashboard.statGamesWon')}</div>
          </div>
        </div>
      {/* Create Game Modal */}
      {showCreateModal && (
        <CreateGameModal
          onClose={() => setShowCreateModal(false)}
          onCreateGame={handleCreateGame}
          user={user}
        />
      )}
    </div>
  );
};

export default Dashboard; 